import type { StatusTone } from '@/shared/ui';

import { getClientFullName } from '@/entities/client/@x/order';
import {
  OrderAcquiringInstrument,
  OrderAuditAction,
  type OrderCustomerSnapshotFieldsFragment,
  type OrderFieldsFragment,
  OrderFiscalFailureKind,
  OrderFiscalOperationStatus,
  OrderFiscalReceiptKind,
  OrderPaymentMethod,
  OrderSource,
  OrderStatus,
} from '@shared/api/graphql/gql/graphql';

/** Как называется заказ на экране: по тому, за что платят. Перенесённые заказы — по наличию записи. */
const ORDER_SOURCE_TITLES: Record<OrderSource, string | null> = {
  [OrderSource.BookingCheckout]: 'Запись',
  [OrderSource.Shop]: 'Покупка',
  [OrderSource.WorkOrder]: 'Заказ-наряд',
  [OrderSource.LegacyImported]: null,
};

export const orderSourceTitle = (order: { source: OrderSource; booking_group_id?: string | null }): string =>
  ORDER_SOURCE_TITLES[order.source] ?? (order.booking_group_id ? 'Запись' : 'Покупка');

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.Draft]: 'Не оплачено',
  [OrderStatus.Paid]: 'Оплачено',
  [OrderStatus.PartiallyRefunded]: 'Частичный возврат',
  [OrderStatus.Refunded]: 'Возвращено',
  [OrderStatus.Cancelled]: 'Отменено',
};

const PAYMENT_METHOD_LABELS: Record<OrderPaymentMethod, string> = {
  [OrderPaymentMethod.Cash]: 'Наличные',
  [OrderPaymentMethod.Card]: 'Карта',
  [OrderPaymentMethod.Online]: 'Оплата по ссылке',
  [OrderPaymentMethod.LegacyUnknown]: 'Способ не указан',
  [OrderPaymentMethod.BankTransfer]: 'Банковский перевод',
};

const ORDER_AUDIT_ACTION_LABELS: Record<OrderAuditAction, string> = {
  [OrderAuditAction.Created]: 'Создана',
  [OrderAuditAction.Refreshed]: 'Данные обновлены',
  [OrderAuditAction.Settled]: 'Оплачена',
  [OrderAuditAction.PartiallyRefunded]: 'Частичный возврат',
  [OrderAuditAction.Refunded]: 'Полный возврат',
  [OrderAuditAction.Cancelled]: 'Отменена',
  [OrderAuditAction.ClientLinked]: 'Привязан клиент',
  [OrderAuditAction.FiscalForceClosed]: 'Фискальная операция аварийно закрыта',
  [OrderAuditAction.FiscalPaymentCancelled]: 'Платёж отменён',
  [OrderAuditAction.FiscalPaymentReturned]: 'Деньги возвращены на карту',
  [OrderAuditAction.FiscalReconciled]: 'Чек подтверждён по ФН',
  [OrderAuditAction.LegacyImported]: 'Импортирована',
};

const ORDER_STATUS_TONES: Record<OrderStatus, StatusTone> = {
  [OrderStatus.Draft]: 'neutral',
  [OrderStatus.Paid]: 'success',
  [OrderStatus.PartiallyRefunded]: 'warning',
  [OrderStatus.Refunded]: 'confirm',
  [OrderStatus.Cancelled]: 'error',
};

export const formatOrderStatus = (status: OrderStatus): string => ORDER_STATUS_LABELS[status];

/** Единый тон статуса заказа (Draft — нейтральный неоплаченный статус). */
export const getOrderStatusTone = (status: OrderStatus): StatusTone => ORDER_STATUS_TONES[status];

export interface OrderFiscalOperationBadge {
  label: string;
  tone: StatusTone;
}

const FISCAL_OPERATION_IN_PROGRESS: OrderFiscalOperationBadge = {
  label: 'В работе',
  tone: 'warning',
};

/** Деньги получены или исход чека неясен: чек допечатает касса, решение принимает Mors. */
const FISCAL_OPERATION_AWAITING_RECEIPT: OrderFiscalOperationBadge = {
  label: 'Ждёт чека',
  tone: 'warning',
};

/** Аварийное закрытие (`OWNER_FORCE_CLOSED`) тоже даёт `FAILED`: деньги учтены по факту, чека нет. */
const FISCAL_OPERATION_FORCE_CLOSED: OrderFiscalOperationBadge = {
  label: 'Закрыта аварийно, чека нет',
  tone: 'warning',
};

/** Как закрыт отменённый платёж: подпись заменяет общее «Не пробит». */
const FISCAL_CANCELLATION_LABELS: Partial<Record<OrderFiscalFailureKind, string>> = {
  [OrderFiscalFailureKind.PaymentReversed]: 'Деньги возвращены клиенту',
  [OrderFiscalFailureKind.CancelledNotCharged]: 'Списания не было',
  [OrderFiscalFailureKind.CancelledUnverified]: 'Отменён без проверки',
  [OrderFiscalFailureKind.ShiftClosedUnconfirmed]: 'Не подтверждён, закрыт со сменой',
};

type FiscalOperationLike = Pick<OrderFieldsFragment['fiscal_operations'][number], 'status' | 'failure_kind'> & {
  receipt_confirmed_at?: string | null;
  receipt_kind?: OrderFiscalReceiptKind | null;
};

/**
 * Бейдж фискальной операции в карточках заказа. Закрытый отказ (`FAILED`) — не повод
 * для проверки: чек не пробит, деньги не списаны, операция завершена. Исключение —
 * аварийное закрытие, см. {@link FISCAL_OPERATION_FORCE_CLOSED}.
 */
const FISCAL_OPERATION_BADGES: Record<OrderFiscalOperationStatus, OrderFiscalOperationBadge> = {
  [OrderFiscalOperationStatus.Completed]: {
    label: 'Зарегистрирован',
    tone: 'success',
  },
  [OrderFiscalOperationStatus.Failed]: {
    label: 'Не пробит',
    tone: 'neutral',
  },
  [OrderFiscalOperationStatus.Prepared]: FISCAL_OPERATION_IN_PROGRESS,
  [OrderFiscalOperationStatus.Started]: FISCAL_OPERATION_IN_PROGRESS,
  [OrderFiscalOperationStatus.PaymentCaptured]: FISCAL_OPERATION_IN_PROGRESS,
  [OrderFiscalOperationStatus.PaymentCapturedUnfiscalized]: FISCAL_OPERATION_AWAITING_RECEIPT,
  [OrderFiscalOperationStatus.ReconciliationRequired]: FISCAL_OPERATION_AWAITING_RECEIPT,
};

export const formatFiscalOperationStatus = (operation: FiscalOperationLike): OrderFiscalOperationBadge => {
  const badge = FISCAL_OPERATION_BADGES[operation.status];
  if (operation.status === OrderFiscalOperationStatus.Completed) {
    if (operation.receipt_confirmed_at) return { ...badge, label: 'Чек подтверждён сотрудником' };
    if (operation.receipt_kind === OrderFiscalReceiptKind.Correction) return { ...badge, label: 'Чек коррекции' };
  }
  if (operation.status !== OrderFiscalOperationStatus.Failed || !operation.failure_kind) return badge;
  if (operation.failure_kind === OrderFiscalFailureKind.OwnerForceClosed) return FISCAL_OPERATION_FORCE_CLOSED;
  const label = FISCAL_CANCELLATION_LABELS[operation.failure_kind];

  return label ? { ...badge, label } : badge;
};

/**
 * `limit` обрезает состав до первых позиций и добавляет общее число — для
 * узких карточек, где полный список всё равно обрежется многоточием.
 */
export const formatOrderLineTitles = (
  lines: readonly { readonly title_snapshot: string; readonly quantity?: number }[],
  limit?: number,
): string => {
  const counts = new Map<string, number>();
  for (const line of lines) {
    counts.set(line.title_snapshot, (counts.get(line.title_snapshot) ?? 0) + (line.quantity ?? 1));
  }

  const titles = [...counts.entries()].map(([title, count]) => (count > 1 ? `${title} ×${count}` : title));

  return limit !== undefined && titles.length > limit
    ? `${titles.slice(0, limit).join(', ')} · всего ${titles.length}`
    : titles.join(', ');
};

/**
 * `instrument` уточняет только безналичную оплату: терминал проводит карту и
 * СБП одним `OrderPaymentMethod.Card`, а различает их производный признак
 * `Order.acquiring_instrument`. Он отсутствует у наличных и у заказов до
 * подключения кассы — тогда безнал остаётся просто «Картой».
 */
export const formatOrderPaymentMethod = (
  method: OrderPaymentMethod,
  instrument?: OrderAcquiringInstrument | null,
): string =>
  method === OrderPaymentMethod.Card && instrument === OrderAcquiringInstrument.Sbp
    ? 'СБП'
    : PAYMENT_METHOD_LABELS[method];

export const formatOrderAuditAction = (action: OrderAuditAction): string => ORDER_AUDIT_ACTION_LABELS[action];

export const formatOrderCustomerName = (snapshot: OrderCustomerSnapshotFieldsFragment | null | undefined): string => {
  if (!snapshot) {
    return 'Без клиента';
  }

  // Единый порядок ФИО с остальными экранами; у компании наименование.
  return getClientFullName(snapshot);
};

export const getOrderNetRevenue = (order: Pick<OrderFieldsFragment, 'total_amount' | 'refunded_amount'>): number =>
  order.total_amount - order.refunded_amount;

export const isOrderFinanciallyLocked = (status: OrderStatus): boolean =>
  status === OrderStatus.Paid || status === OrderStatus.PartiallyRefunded;
